import { Briefcase, CalendarDays, ExternalLink, Link2, MapPin } from 'lucide-react'
import Lang from '../../../config/lang'
import { formatLocalizedDate } from '../applicationMapper'

const SummaryItem = ({
  icon,
  iconWrapperClassName,
  label,
  value,
  helperText,
  href,
  hrefLabel,
  containerClassName = '',
  valueClassName = 'break-words',
}) => (
  <div className={`flex min-w-0 items-start gap-3 ${containerClassName}`}>
    <div
      className={`flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-[12px] ${iconWrapperClassName}`}
    >
      {icon}
    </div>

    <div className='min-w-0'>
      <p className='text-[12px] font-normal leading-4 text-[#6B7280]'>
        {label}
      </p>
      <p
        className={`mt-[2px] text-[14px] font-medium leading-5 text-[#111827] ${valueClassName}`}
      >
        {value}
      </p>

      {helperText ? (
        <p className='mt-[2px] text-[12px] font-normal leading-5 text-[#6B7280]'>
          {helperText}
        </p>
      ) : null}

      {href ? (
        <a
          href={href}
          target='_blank'
          rel='noreferrer'
          className='mt-[4px] inline-flex max-w-full items-center gap-[4px] break-all text-[12px] font-normal leading-[16.5px] text-[#185FA5] transition hover:text-[#185FA5]'
        >
          {hrefLabel}
          <ExternalLink className='h-[11px] w-[11px]' strokeWidth={2} />
        </a>
      ) : null}
    </div>
  </div>
)

const ApplicationMetaSummary = ({
  variant = 'default',
  dateApplied,
  location,
  lang,
  interviewMethod,
  interviewMethodNote,
  interviewLocation,
  interviewLink,
  mapUrl,
  linkUrl,
}) => {
  const dateAppliedLabel =
    dateApplied && dateApplied != '-'
      ? formatLocalizedDate(dateApplied, lang, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '-'
  const interviewMethodText =
    interviewMethod != null && String(interviewMethod).trim() != ''
      ? String(interviewMethod).trim()
      : '-'
  const isOnlineInterview = interviewMethodText.toLowerCase() == 'online'
  const interviewLocationText =
    interviewLocation != null && String(interviewLocation).trim() != ''
      ? String(interviewLocation).trim()
      : '-'
  const interviewLinkText =
    interviewLink != null && String(interviewLink).trim() != ''
      ? String(interviewLink).trim()
      : '-'
  const detailLabel = isOnlineInterview
    ? Lang(lang, 'interview_link')
    : Lang(lang, 'location')
  const detailValue = isOnlineInterview
    ? interviewLinkText
    : interviewLocationText
  const detailHref = isOnlineInterview ? linkUrl : mapUrl
  const detailHrefLabel = isOnlineInterview
    ? Lang(lang, 'open_link')
    : Lang(lang, 'open_maps')

  if (variant == 'interview') {
    return (
      <div className='flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-6 sm:gap-y-5'>
        <SummaryItem
          label={Lang(lang, 'interview_mode')}
          value={interviewMethodText}
          helperText={
            isOnlineInterview ||
            interviewMethodNote == null ||
            String(interviewMethodNote).trim() == ''
              ? ''
              : String(interviewMethodNote).trim()
          }
          iconWrapperClassName='bg-[#F7EEDC]'
          icon={
            <Briefcase
              className='h-[14px] w-[14px] text-[#C27A0A]'
              strokeWidth={1.8}
            />
          }
        />

        <SummaryItem
          label={detailLabel}
          value={detailValue}
          href={detailHref}
          hrefLabel={detailHrefLabel}
          containerClassName='sm:max-w-[360px]'
          valueClassName={isOnlineInterview ? 'break-all' : 'break-words'}
          iconWrapperClassName='bg-[#EAF3DE]'
          icon={
            isOnlineInterview ? (
              <Link2
                className='h-[14px] w-[14px] text-[#639922]'
                strokeWidth={1.8}
              />
            ) : (
              <MapPin
                className='h-[14px] w-[14px] text-[#639922]'
                strokeWidth={1.8}
              />
            )
          }
        />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-6 sm:gap-y-4'>
      <SummaryItem
        label={Lang(lang, 'location')}
        value={location || '-'}
        iconWrapperClassName='bg-[#EAF3DE]'
        icon={
          <MapPin
            className='h-[14px] w-[14px] text-[#639922]'
            strokeWidth={2}
          />
        }
      />

      <SummaryItem
        label={Lang(lang, 'date_applied')}
        value={dateAppliedLabel}
        iconWrapperClassName='bg-[#FEFCE8]'
        icon={
          <CalendarDays
            className='h-[14px] w-[14px] text-[#CA8A04]'
            strokeWidth={2}
          />
        }
      />
    </div>
  )
}

export default ApplicationMetaSummary
